import { useState } from 'react';
import {
  makeStyles,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Badge
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FiberNew as NewSectionIcon,
  Schedule as ScheduleIcon,
  ChevronRight as ChevronRightIcon
} from '@material-ui/icons';
import { changesData, changeColors, getChangesCounts } from './changesData';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 1400,
    margin: '0 auto',
  },
  header: {
    marginBottom: theme.spacing(5),
    paddingBottom: theme.spacing(3),
    borderBottom: '1px solid #e2e8f0'
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.025em'
  },
  subtitle: {
    fontSize: '1rem',
    color: '#64748b',
    fontWeight: 400,
    lineHeight: 1.5
  },
  tabs: {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    marginBottom: theme.spacing(4),
    overflow: 'hidden',
    '& .MuiTabs-indicator': {
      backgroundColor: '#1e293b',
      height: 3
    },
    '& .MuiTab-root': {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      minWidth: 'auto',
      padding: '16px 32px',
      color: '#64748b',
      transition: 'all 0.2s ease',
      '&.Mui-selected': {
        color: '#1e293b',
        backgroundColor: '#f8fafc'
      },
      '&:hover': {
        backgroundColor: '#f1f5f9',
        color: '#334155'
      }
    }
  },
  currentReleaseCard: {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    marginBottom: theme.spacing(4),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)'
    }
  },
  releaseCard: {
    marginBottom: theme.spacing(3),
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    '&:hover': {
      borderColor: '#cbd5e1',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-1px)'
    }
  },
  releaseHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #f1f5f9'
  },
  versionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  versionTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.02em'
  },
  versionDate: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontWeight: 500
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    '&.draft': {
      backgroundColor: '#fef3c7',
      color: '#92400e',
      border: '1px solid #fcd34d'
    },
    '&.released': {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #6ee7b7'
    }
  },
  changesList: {
    padding: 0,
    marginTop: theme.spacing(2)
  },
  changeItem: {
    padding: theme.spacing(2, 3),
    margin: theme.spacing(1, 0),
    borderRadius: '8px',
    border: '1px solid #f1f5f9',
    borderLeft: '4px solid',
    backgroundColor: '#fafafa',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8fafc',
      borderColor: '#e2e8f0',
      transform: 'translateX(4px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      '& $navigationIcon': {
        color: '#64748b',
        transform: 'translateX(2px)'
      }
    },
    '&.added': {
      borderLeftColor: changeColors.added.color,
      '&:hover': {
        backgroundColor: changeColors.added.bg
      }
    },
    '&.modified': {
      borderLeftColor: changeColors.modified.color,
      '&:hover': {
        backgroundColor: changeColors.modified.bg
      }
    },
    '&.deleted': {
      borderLeftColor: changeColors.deleted.color,
      '&:hover': {
        backgroundColor: changeColors.deleted.bg
      }
    },
    '&.new-section': {
      borderLeftColor: changeColors.newSection.color,
      '&:hover': {
        backgroundColor: changeColors.newSection.bg
      }
    },
    '&.disabled': {
      cursor: 'default',
      opacity: 0.7,
      '&:hover': {
        backgroundColor: '#fafafa',
        borderColor: '#f1f5f9',
        transform: 'none',
        boxShadow: 'none',
        '& $navigationIcon': {
          color: '#94a3b8',
          transform: 'none'
        }
      }
    }
  },
  changeIcon: {
    minWidth: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
      fontSize: '1.1rem'
    },
    '&.added': { color: changeColors.added.color },
    '&.modified': { color: changeColors.modified.color },
    '&.deleted': { color: changeColors.deleted.color },
    '&.new-section': { color: changeColors.newSection.color }
  },
  changeContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  changeTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#0f172a',
    lineHeight: 1.4
  },
  changeDescription: {
    fontSize: '0.8rem',
    color: '#64748b',
    lineHeight: 1.4
  },
  changeDocument: {
    fontSize: '0.75rem',
    color: '#94a3b8',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8),
    color: '#94a3b8',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #e2e8f0'
  },
  currentReleaseContent: {
    padding: theme.spacing(4),
    color: '#0f172a'
  },
  currentReleaseTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  currentReleaseSubtitle: {
    fontSize: '0.95rem',
    color: '#64748b',
    fontWeight: 400,
    marginBottom: theme.spacing(3)
  },
  navigationIcon: {
    color: '#94a3b8',
    fontSize: '1.2rem',
    marginLeft: theme.spacing(1),
    transition: 'all 0.2s ease',
    transform: 'translateX(0)'
  }
}));

const ReleaseHistory = ({ onNavigateToDocument }) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);

  const getChangeIcon = (type) => {
    switch (type) {
      case 'added': return <AddIcon />;
      case 'modified': return <EditIcon />;
      case 'deleted': return <DeleteIcon />;
      case 'new-section': return <NewSectionIcon />;
      default: return <EditIcon />;
    }
  };

  const getChangeTypeText = (type) => {
    switch (type) {
      case 'added': return 'Added';
      case 'modified': return 'Modified';
      case 'deleted': return 'Deleted';
      case 'new-section': return 'New Section';
      default: return 'Modified';
    }
  };

  const handleChangeClick = (change) => {
    if (onNavigateToDocument && change.documentId) {
      onNavigateToDocument(change.documentId, change.id);
    }
  };

  const renderChangesList = (changes, isCurrentRelease = false) => {
    if (!changes || changes.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="h6" style={{ color: '#64748b', marginBottom: 8 }}>
            No changes yet
          </Typography>
          <Typography variant="body2" style={{ color: '#94a3b8' }}>
            Changes to documentation will appear here as they are made
          </Typography>
        </Box>
      );
    }

    return (
      <List className={classes.changesList}>
        {changes.map((change) => (
          <ListItem
            key={change.id}
            className={`${classes.changeItem} ${change.type} ${!isCurrentRelease ? 'disabled' : ''}`}
            onClick={isCurrentRelease ? () => handleChangeClick(change) : undefined}
          >
            <ListItemIcon className={`${classes.changeIcon} ${change.type}`}>
              {getChangeIcon(change.type)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box className={classes.changeContent}>
                  <Typography className={classes.changeTitle}>
                    {change.title}
                  </Typography>
                  <Typography className={classes.changeDocument}>
                    {change.documentId} → {change.section}
                  </Typography>
                  <Typography className={classes.changeDescription}>
                    {change.description}
                  </Typography>
                </Box>
              }
            />
            <Box display="flex" alignItems="center" ml={2}>
              <Chip
                size="small"
                label={getChangeTypeText(change.type)}
                style={{
                  backgroundColor: changeColors[change.type.replace('-', '')]?.color || changeColors.modified.color,
                  color: 'white',
                  fontSize: '0.7rem',
                  fontWeight: 600
                }}
              />
              {isCurrentRelease && <ChevronRightIcon className={classes.navigationIcon} />}
            </Box>
          </ListItem>
        ))}
      </List>
    );
  };


  const renderCurrentRelease = () => {
    const counts = getChangesCounts();

    return (
      <Box>
        <Card className={classes.currentReleaseCard}>
          <CardContent className={classes.currentReleaseContent}>
            <Box className={classes.releaseHeader}>
              <Box className={classes.versionInfo}>
                <Typography className={classes.currentReleaseTitle}>
                  <ScheduleIcon />
                  Current Release
                </Typography>
                <Typography className={classes.currentReleaseSubtitle}>
                  Changes that will be included in the next release
                </Typography>
              </Box>
              <Chip
                label="In Development"
                className={`${classes.statusChip} draft`}
              />
            </Box>

            {/* Inline stats - only show if there are changes */}
            {counts.total > 0 && (
              <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
                {counts.added > 0 && (
                  <Chip
                    size="small"
                    label={`${counts.added} added`}
                    style={{
                      backgroundColor: '#f0fdf4',
                      color: changeColors.added.color,
                      border: `1px solid ${changeColors.added.color}`,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  />
                )}
                {counts.modified > 0 && (
                  <Chip
                    size="small"
                    label={`${counts.modified} modified`}
                    style={{
                      backgroundColor: '#fffbeb',
                      color: changeColors.modified.color,
                      border: `1px solid ${changeColors.modified.color}`,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  />
                )}
                {counts.deleted > 0 && (
                  <Chip
                    size="small"
                    label={`${counts.deleted} deleted`}
                    style={{
                      backgroundColor: '#fef2f2',
                      color: changeColors.deleted.color,
                      border: `1px solid ${changeColors.deleted.color}`,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  />
                )}
                {counts.newSection > 0 && (
                  <Chip
                    size="small"
                    label={`${counts.newSection} new sections`}
                    style={{
                      backgroundColor: '#eff6ff',
                      color: changeColors.newSection.color,
                      border: `1px solid ${changeColors.newSection.color}`,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {renderChangesList(changesData.currentRelease.changes, true)}
      </Box>
    );
  };

  const renderReleaseHistory = () => (
    <Box>
      {changesData.releases.map((release) => {
        const counts = getChangesCounts(null, release.version);

        return (
          <Card key={release.version} className={classes.releaseCard}>
            <CardContent>
              <Box className={classes.releaseHeader}>
                <Box className={classes.versionInfo}>
                  <Typography className={classes.versionTitle}>
                    Release {release.version}
                  </Typography>
                  <Typography className={classes.versionDate}>
                    {release.date}
                  </Typography>
                </Box>
                <Chip
                  label="Released"
                  className={`${classes.statusChip} released`}
                />
              </Box>

              {release.summary && (
                <Typography variant="body2" style={{
                  marginBottom: 24,
                  color: '#64748b',
                  fontSize: '0.9rem',
                  lineHeight: 1.5
                }}>
                  {release.summary}
                </Typography>
              )}

              <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
                {counts.added > 0 && (
                  <Chip
                    size="small"
                    label={`${counts.added} added`}
                    style={{
                      backgroundColor: '#f0fdf4',
                      color: changeColors.added.color,
                      border: `1px solid ${changeColors.added.color}`,
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}
                  />
                )}
                {counts.modified > 0 && (
                  <Chip
                    size="small"
                    label={`${counts.modified} modified`}
                    style={{
                      backgroundColor: '#fffbeb',
                      color: changeColors.modified.color,
                      border: `1px solid ${changeColors.modified.color}`,
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}
                  />
                )}
                {counts.newSection > 0 && (
                  <Chip
                    size="small"
                    label={`${counts.newSection} new sections`}
                    style={{
                      backgroundColor: '#eff6ff',
                      color: changeColors.newSection.color,
                      border: `1px solid ${changeColors.newSection.color}`,
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>
              {renderChangesList(release.changes, false)}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );

  return (
    <Box className={classes.root}>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        className={classes.tabs}
        indicatorColor="primary"
      >
        <Tab
          label={
            changesData.currentRelease.changes.length > 0 ? (
              <Badge badgeContent={changesData.currentRelease.changes.length} color="primary">
                Current Release
              </Badge>
            ) : (
              "Current Release"
            )
          }
        />
        <Tab label="Release History" />
      </Tabs>

      {activeTab === 0 && renderCurrentRelease()}
      {activeTab === 1 && renderReleaseHistory()}
    </Box>
  );
};

export default ReleaseHistory;
